import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sun, Moon, LogOut, User, ArrowLeft } from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import NotificationBell from './NotificationBell';

interface WorkdayClientHeaderProps {
  showBack?: boolean;
  hideNav?: boolean;
  logoSrc?: string;
}

export default function WorkdayClientHeader({ showBack, hideNav, logoSrc }: WorkdayClientHeaderProps) {
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminPlanParam = searchParams.get('adminPlan');
  const dashboardHref = adminPlanParam ? `/workday-client/dashboard?adminPlan=${adminPlanParam}` : '/workday-client/dashboard';
  const settingsHref = adminPlanParam ? `/workday-client/settings?adminPlan=${adminPlanParam}` : '/workday-client/settings';

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) fetchPhoto();
  }, [user?.id]);

  const fetchPhoto = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('workday_client_profiles')
      .select('photo_url')
      .eq('id', user.id)
      .maybeSingle();
    if (data?.photo_url) {
      const { data: urlData } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(data.photo_url);
      setPhotoUrl(urlData.publicUrl);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  return (
    <header className="fixed w-full z-50 bg-black backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <div className="flex-1 flex items-center justify-between">
            {/* Logo */}
            {hideNav ? (
              <div className="-mt-4 -mb-4">
                <img src={logoSrc ?? '/1.png'} alt="PerplexityATS" className="h-28 sm:h-32 w-auto object-contain" />
              </div>
            ) : (
              <Link to={dashboardHref} className="-mt-4 -mb-4 flex items-center">
                <img src={logoSrc ?? '/1.png'} alt="PerplexityATS" className="h-28 sm:h-32 w-auto object-contain" />
              </Link>
            )}

            {/* Right controls */}
            {!hideNav && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={toggleTheme}
                  className="text-gray-300 hover:text-white transition-colors p-2"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <div className="text-white">
                  <NotificationBell />
                </div>

                {/* Profile avatar / back button */}
                {showBack ? (
                  <button
                    onClick={() => navigate(dashboardHref)}
                    className="text-gray-300 hover:text-white transition-colors p-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(settingsHref)}
                    className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden border-2 border-gray-600 hover:border-[#40E0D0] transition-colors flex-shrink-0"
                    title="Account Settings"
                  >
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  className="text-gray-300 hover:text-white transition-colors p-2"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
